import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useState, useRef, useCallback} from 'react';
import _ from 'underscore';

import {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import {
  suggestDataEntryClarifications,
  SuggestDataEntryClarificationsResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {RED} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as ImportUtil from 'util/ImportUtil';

import isStructuredTableColumnFillable from './isStructuredTableColumnFillable';

export enum SuggestedClarificationsStateType {
  UNSET = 'UNSET',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  INSTRUCTIONS_ONLY = 'INSTRUCTIONS_ONLY',
}

export type AIDataEntrySuggestedClarificationsState =
  | {type: SuggestedClarificationsStateType.UNSET}
  | {type: SuggestedClarificationsStateType.INSTRUCTIONS_ONLY}
  | {type: SuggestedClarificationsStateType.PENDING}
  | {
      type: SuggestedClarificationsStateType.SUCCESS;
      response: SuggestDataEntryClarificationsResponse;
      userSpecifiedRows: {[tableName: string]: Array<{text: string}>} | null;
    }
  | {type: SuggestedClarificationsStateType.FAILURE};

/**
 * State and actions for the suggested clarifications system.
 */
export interface AIDataEntrySuggestedClarificationsControls {
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState;
  setSuggestedClarificationsState: (state: AIDataEntrySuggestedClarificationsState) => void;
  suggestClarifications: (
    entryId: string,
    tableNames: Array<string>,
    selectedFiles: Array<AIModalSelectedFile>,
    promptText: string
  ) => Promise<void>;
  clearSuggestedClarifications: () => void;
}

/**
 * Custom hook for managing both server and client state for suggested clarifications.
 */
export function useAIDataEntrySuggestedClarificationsControls(): AIDataEntrySuggestedClarificationsControls {
  const [suggestedClarificationsState, setSuggestedClarificationsState] =
    useState<AIDataEntrySuggestedClarificationsState>({
      type: SuggestedClarificationsStateType.UNSET,
    });
  // Track unique request IDs to properly account for concurrent requests.
  const latestRequestIdRef = useRef<string | null>(null);

  const suggestClarifications = useCallback(
    async (
      entryId: string,
      tableNames: Array<string>,
      selectedFiles: Array<AIModalSelectedFile>,
      promptText: string
    ): Promise<void> => {
      const requestId = _.uniqueId('suggest-clarifications');
      latestRequestIdRef.current = requestId;

      setSuggestedClarificationsState({type: SuggestedClarificationsStateType.PENDING});
      let response: SuggestDataEntryClarificationsResponse;
      try {
        const files = await Promise.all(
          selectedFiles.map(async ({file}) => ({
            s3Location: (await ImportUtil.upload(file))!,
            mediaType: file.type,
          }))
        );
        response = await suggestDataEntryClarifications(entryId, tableNames, files, promptText);
      } catch (e) {
        if (latestRequestIdRef.current === requestId) {
          setSuggestedClarificationsState({type: SuggestedClarificationsStateType.FAILURE});
        }
        throw e;
      }
      if (latestRequestIdRef.current === requestId) {
        const userSpecifiedRows: {[tableName: string]: Array<{text: string}>} = {};
        if (response.suggestedFirstRows) {
          (response.tableOrder || tableNames).forEach((tableName, index) => {
            userSpecifiedRows[tableName] = response.suggestedFirstRows![index] || [];
          });
        }

        setSuggestedClarificationsState({
          type: SuggestedClarificationsStateType.SUCCESS,
          response,
          userSpecifiedRows,
        });
      }
    },
    [latestRequestIdRef]
  );

  const clearSuggestedClarifications = useCallback(() => {
    // Reset the request ID so that any previous requests are discarded.
    latestRequestIdRef.current = null;
    setSuggestedClarificationsState({type: SuggestedClarificationsStateType.UNSET});
  }, []);

  return {
    suggestedClarificationsState,
    setSuggestedClarificationsState,
    suggestClarifications,
    clearSuggestedClarifications,
  };
}

/**
 * If applicable, append the example row to the end of the user-provided prompt to create a combined
 * prompt to send to the LLM.
 */
export function getUpdatedPromptTextWithSuggestedClarifications(
  promptText: string,
  structuredTables: Array<StructuredTable<unknown, unknown>>,
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState
): string {
  if (suggestedClarificationsState.type !== SuggestedClarificationsStateType.SUCCESS) {
    return promptText;
  }
  const {userSpecifiedRows} = suggestedClarificationsState;
  if (!userSpecifiedRows) {
    return promptText;
  }
  const suffix = Object.entries(userSpecifiedRows)
    .map(
      ([tableName, userSpecifiedRow]) => `\
Example row for ${tableName}:
${userSpecifiedRow
  .flatMap((cell, i) => {
    const column = structuredTables.find((table) => table.name === tableName)?.columns[i];
    if (column && isStructuredTableColumnFillable(column)) {
      const cellText = cell.text === '' ? '[Leave blank]' : cell.text;
      return [`${column.name}: ${cellText}`];
    }
    return [];
  })
  .join('\n')}
`
    )
    .join('\n\n');
  if (promptText.trim()) {
    return `${promptText}\n\n${suffix}`;
  } else {
    return suffix;
  }
}

interface AIDataEntrySuggestedClarificationsProps {
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState;
  structuredTables: Array<StructuredTable<unknown, unknown>>;
  onUpdateSuggestedClarificationsState: (state: AIDataEntrySuggestedClarificationsState) => void;
  onSuggestClarifications: () => void;
}

/**
 * Display auto-suggested clarifications for the user to review and refine.
 *
 * Currently the only suggested clarification is an example row, which seems to provide most of the
 * value in clarifying the task, though we may add more suggestions here.
 */
export default function AIDataEntrySuggestedClarifications({
  suggestedClarificationsState,
  structuredTables,
  onUpdateSuggestedClarificationsState,
  onSuggestClarifications,
}: AIDataEntrySuggestedClarificationsProps): JSX.Element | null {
  if (suggestedClarificationsState.type === SuggestedClarificationsStateType.UNSET) {
    return <div>Upload a file above or write instructions below to generate an example row.</div>;
  } else if (suggestedClarificationsState.type === SuggestedClarificationsStateType.INSTRUCTIONS_ONLY) {
    return (
      <Button
        buttonStyle={ButtonStyle.LINK}
        icon="refresh"
        text="Generate example row"
        onClick={onSuggestClarifications}
      />
    );
  } else if (suggestedClarificationsState.type === SuggestedClarificationsStateType.PENDING) {
    return (
      <div className={css(styles.loadingContainer)}>
        <HorizontalThrobber />
        <span className={css(styles.loadingText)}>Analyzing data to suggest an example row...</span>
      </div>
    );
  } else if (suggestedClarificationsState.type === SuggestedClarificationsStateType.FAILURE) {
    return (
      <div>
        <div>
          <span className={css(styles.errorIcon)}>
            <BenchlingIcon icon="error" />
          </span>
          An error occurred while generating an example row.{' '}
          <Button
            buttonStyle={ButtonStyle.LINK}
            icon="refresh"
            text="Retry"
            onClick={onSuggestClarifications}
            textClassName={css(styles.retryText)}
          />
        </div>
      </div>
    );
  } else {
    const {
      response: {errorMessage, hadError},
      userSpecifiedRows,
    } = suggestedClarificationsState;

    if (hadError) {
      return (
        <div>
          <div>
            <span className={css(styles.errorIcon)}>
              <BenchlingIcon icon="error" />
            </span>
            {errorMessage ? (
              <span>
                <span className={css(styles.errorMessage)}>Problem encountered: </span>
                {errorMessage}
              </span>
            ) : (
              <span>An error occurred while generating an example row.</span>
            )}
          </div>
          <div>
            <Button
              buttonStyle={ButtonStyle.LINK}
              icon="refresh"
              text="Retry"
              onClick={onSuggestClarifications}
              textClassName={css(styles.retryText)}
            />
          </div>
        </div>
      );
    }

    const isFillableByColumnIndexPerTable: {[tableName: string]: {[columnIndex: number]: boolean}} = {};
    for (const structuredTable of structuredTables) {
      isFillableByColumnIndexPerTable[structuredTable.name] = {};
      for (let i = 0; i < structuredTable.columns.length; i++) {
        isFillableByColumnIndexPerTable[structuredTable.name][i] = isStructuredTableColumnFillable(
          structuredTable.columns[i]
        );
      }
    }
    return (
      <div>
        <div>Review the example row below and edit to make any corrections.</div>
        {Object.entries(userSpecifiedRows || {}).map(([tableName, userSpecifiedRow], tableIndex) => {
          const structuredTable = structuredTables.find((t) => t.name === tableName);
          return (
            <div className="mediocre" key={`suggested-clarifications-${tableName}`}>
              {structuredTable ? (
                <SimpleTableEditable
                  {...standaloneProps}
                  cid="tmp-flexible-data-entry-suggested-clarifications"
                  locked={false}
                  table={{
                    name: `Example row for ${tableName}`,
                    columnLabels: structuredTable.columns.map((c) => c.name),
                    table: [
                      userSpecifiedRow.map((cell, i) => ({
                        ...cell,
                        locked: !isFillableByColumnIndexPerTable[tableName][i],
                      })),
                    ],
                    widths: structuredTable.columns.map((c) => c.width),
                    columnConstraints: structuredTable.columns.map((c) => null),
                    allowAddRows: false,
                    lockedDims: true,
                  }}
                  hideFormulaBox={true}
                  enableFormulas={false}
                  enableFillDown={false}
                  storeState={noop}
                  updateHandler={(index, {table}) => {
                    const updatedRows = {...userSpecifiedRows};
                    updatedRows[tableName] = table.table[0];
                    onUpdateSuggestedClarificationsState({
                      type: SuggestedClarificationsStateType.SUCCESS,
                      response: suggestedClarificationsState.response,
                      userSpecifiedRows: updatedRows,
                    });
                  }}
                />
              ) : null}
            </div>
          );
        })}

        <div>
          <Button
            buttonStyle={ButtonStyle.LINK}
            icon="refresh"
            text="Generate new example row based on latest instructions"
            onClick={onSuggestClarifications}
            textClassName={css(styles.retryText)}
          />
        </div>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  loadingText: {
    marginLeft: 4,
  },
  errorIcon: {
    color: RED,
    marginRight: 4,
  },
  errorMessage: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  retryText: {
    marginLeft: 4,
  },
});
